/**
 * Chart option construction and value formatting.
 *
 * Kept out of the component file so these can be imported and tested without
 * mounting anything: a chart that plots the wrong series is not a failure a
 * render smoke test would catch.
 *
 * The visual treatment lives here too, and is mostly subtraction. Tick marks,
 * the y-axis line and the vertical split lines all carry no information, so
 * removing them leaves the reader with the data and the few lines needed to
 * measure it against. What is added — rounded bar caps, a faded area under a
 * line, a gap between donut slices — exists to separate marks from each other,
 * not to decorate them.
 */
import type { EChartsCoreOption } from "echarts/core";
import { chartTheme, type ChartTheme } from "./chartTheme";
import { formatNumber } from "./numberFormat";
import type { ChartType, PanelEncoding } from "../types";

/** The only chart types drawn through ECharts. */
export const ECHARTS_TYPES: ReadonlySet<ChartType> = new Set<ChartType>([
  "bar",
  "line",
  "pie",
]);

/** ECharts needs a font stack as a string; this matches the one in styles.css. */
const FONT = '"Segoe UI", system-ui, -apple-system, sans-serif';

/** Beyond this an axis category is truncated rather than rotated further. */
const CATEGORY_LABEL_LIMIT = 16;

/** Bars wider than this read as blocks rather than as a series. */
const BAR_MAX_WIDTH = 42;

/** Beyond this many categories an axis becomes scrollable instead of crowded. */
const ZOOM_CATEGORY_THRESHOLD = 24;

/** Average glyph width of 11px Segoe UI, for estimating tick label extents. */
const CHAR_WIDTH = 6.6;
/** Gap between the category axis line and its labels. */
const X_LABEL_MARGIN = 11;
/** Default ECharts gap between a value axis and its tick labels. */
const Y_LABEL_MARGIN = 8;
/** Clearance between the tick labels and the axis title beside them. */
const TITLE_CLEARANCE = Y_LABEL_MARGIN + 8;
/** Room kept outside the labels for an axis title's own line height. */
const TITLE_ROOM = 22;

/** The widest tick label a value axis will draw for these values. */
function valueLabelWidth(values: number[]): number {
  const largest = values.reduce((max, value) => Math.max(max, Math.abs(value)), 0);
  // Ticks run a step past the largest value, so measure one step beyond it.
  const sample = formatCompact(largest * 1.25);
  const negative = values.some((value) => value < 0) ? 1 : 0;
  return Math.max(sample.length + negative, 1) * CHAR_WIDTH;
}

/** How far rotated or flat category labels extend below the axis line. */
function categoryLabelHeight(categories: string[], rotation: number): number {
  if (!rotation) return 14;
  const longest = Math.min(
    Math.max(1, ...categories.map((label) => label.length)),
    CATEGORY_LABEL_LIMIT + 1,
  );
  const radians = (Math.abs(rotation) * Math.PI) / 180;
  return longest * CHAR_WIDTH * Math.sin(radians) + 11 * Math.cos(radians);
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Words that read wrongly in title case: "Usd", "Sla", "P1". */
const ACRONYMS = new Set([
  "acw", "aht", "api", "eur", "gbp", "id", "inr", "ivr", "kpi", "mos",
  "mtd", "p1", "p2", "p3", "p4", "qtd", "roi", "sla", "sql", "ttf", "usd",
  "ytd",
]);

const CURRENCIES: Record<string, string> = {
  usd: "USD",
  eur: "EUR",
  gbp: "GBP",
  inr: "INR",
};

const MONTH_NUMBER_COLUMNS = new Set([
  "month", "calendar_month", "month_number", "month_num", "month_no",
  "monthnumber",
]);
const MONTH_NAME_COLUMNS = new Set([
  "month_name", "monthname", "calendar_month_name",
]);

// Cube returns naive timestamps in the query's timezone, which is UTC here.
const ISO_DATE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

// Built by hand rather than with Intl: ICU versions disagree on "Sep" versus
// "Sept", and the exported documents must print exactly what the screen does.
const pad = (value: number) => String(value).padStart(2, "0");

function leafOf(column: string): string {
  return (column.split(".").pop() ?? column).toLowerCase();
}

function tokensOf(column: string): string[] {
  return leafOf(column).split(/[^a-z0-9]+/).filter(Boolean);
}

function isYearColumn(column: string): boolean {
  const leaf = leafOf(column);
  return leaf === "year" || leaf.endsWith("_year");
}

function isMonthNumberColumn(column: string): boolean {
  // `fact.date.month` is a timestamp bucketed by month, not a month number.
  return MONTH_NUMBER_COLUMNS.has(leafOf(column)) && !granularityOf(column);
}

function isMonthNameColumn(column: string): boolean {
  return MONTH_NAME_COLUMNS.has(leafOf(column));
}

// Fiscal periods count from the organisation's own year start, so they are
// labelled as fiscal and never translated into calendar month names.
const FISCAL_YEAR_COLUMNS = new Set(["fiscal_year", "fy"]);
const FISCAL_QUARTER_COLUMNS = new Set(["fiscal_quarter", "fiscal_qtr", "fq"]);
// A bare "period" numbered 1 to 13 is the ledger's accounting period.
const FISCAL_PERIOD_COLUMNS = new Set([
  "fiscal_month", "fiscal_period", "fiscal_month_number", "fp",
  "period", "period_number", "fiscal_period_number", "accounting_period",
  "posting_period",
]);

function isFiscalYearColumn(column: string): boolean {
  return FISCAL_YEAR_COLUMNS.has(leafOf(column)) || isYearColumn(column);
}

/** A fiscal period or quarter: an ordinal within a year, read in order. */
function isFiscalOrdinalColumn(column: string): boolean {
  const leaf = leafOf(column);
  return FISCAL_PERIOD_COLUMNS.has(leaf) || FISCAL_QUARTER_COLUMNS.has(leaf);
}

/** A year from 2025 or "FY2025", or null. */
function yearKey(value: unknown): number | null {
  const direct = toNumber(value);
  if (direct !== null) return direct;
  const digits = /(\d{4})/.exec(String(value ?? ""));
  return digits ? Number(digits[1]) : null;
}

/** `FY2025`, `FY Q3` or `FY P07` for a whole-number fiscal value. */
export function fiscalLabel(value: unknown, column: string): string | null {
  if (typeof value === "object" || typeof value === "boolean") return null;
  const number = toNumber(value);
  if (number === null || !Number.isInteger(number)) return null;
  const leaf = leafOf(column);
  if (FISCAL_YEAR_COLUMNS.has(leaf)) return `FY${number}`;
  if (FISCAL_QUARTER_COLUMNS.has(leaf) && number >= 1 && number <= 4) {
    return `FY Q${number}`;
  }
  if (FISCAL_PERIOD_COLUMNS.has(leaf) && number >= 1 && number <= 13) {
    return `FY P${pad(number)}`;
  }
  return null;
}

/** A missing or non-numeric measure is a gap, never an invented zero. */
function toNumber(value: unknown): number | null {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    typeof value === "boolean"
  ) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** An ISO date or timestamp as a reader writes it, or null if it is not one. */
export function formatDate(value: string): string | null {
  const text = value.trim();
  const match = ISO_DATE.exec(text);
  if (!match) return null;
  const [, year, month, day, hour, minute, second, zone] = match;
  const parsed = new Date(
    zone
      ? text.replace(" ", "T")
      : `${year}-${month}-${day}T${hour ?? "00"}:${minute ?? "00"}:${second ?? "00"}Z`,
  );
  if (Number.isNaN(parsed.getTime())) return null;
  const date = `${pad(parsed.getUTCDate())} ${
    MONTH_NAMES[parsed.getUTCMonth()]
  } ${parsed.getUTCFullYear()}`;
  const midnight =
    parsed.getUTCHours() === 0 &&
    parsed.getUTCMinutes() === 0 &&
    parsed.getUTCSeconds() === 0;
  return midnight
    ? date
    : `${date}, ${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())} UTC`;
}

function monthName(value: unknown): string | null {
  const number = Number(value);
  return Number.isInteger(number) && number >= 1 && number <= 12
    ? MONTH_NAMES[number - 1] ?? null
    : null;
}

function monthIndex(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 12) {
    return numeric;
  }
  const prefix = String(value).trim().slice(0, 3).toLowerCase();
  const index = MONTH_NAMES.findIndex(
    (name) => name.toLowerCase() === prefix,
  );
  return index >= 0 ? index + 1 : null;
}

/**
 * Rows in calendar order when a month column drives the view.
 *
 * Engines sort month names alphabetically, so April led August on every
 * trend. Only reorders when every month value is recognisable; anything else
 * keeps the engine's order, which may be a deliberate ranking.
 */
export function chronologicalRows(
  rows: Record<string, unknown>[],
  columns: string[],
): Record<string, unknown>[] {
  const calendar =
    columns.find(isMonthNameColumn) ?? columns.find(isMonthNumberColumn);
  // Fiscal periods arrive in whatever order the engine grouped them, which
  // read as 9, 7, 2, 12 along a trend.
  const month = calendar ?? columns.find(isFiscalOrdinalColumn);
  if (!month) return rows;
  const year = columns.find(isFiscalYearColumn);
  const position = (value: unknown) => {
    if (calendar) return monthIndex(value);
    const number = toNumber(value);
    return number !== null && Number.isInteger(number) ? number : null;
  };
  const keyed = rows.map((row) => ({
    row,
    month: position(row[month]),
    year: year ? yearKey(row[year]) : 0,
  }));
  if (keyed.some((item) => item.month === null || item.year === null)) {
    return rows;
  }
  return keyed
    .sort((a, b) => (a.year ?? 0) - (b.year ?? 0) || (a.month ?? 0) - (b.month ?? 0))
    .map((item) => item.row);
}

/** Metric member to the unit the context layer certifies it in. */
export type UnitMap = Readonly<Record<string, string>>;

/** The context layer's unit vocabulary, written the way a reader expects. */
const UNIT_LABELS: Record<string, string> = {
  percent: "%",
  milliseconds: "ms",
};

export function unitLabel(unit: string): string {
  return UNIT_LABELS[unit] ?? unit;
}

/**
 * The unit a member is measured in.
 *
 * The certified unit wins. Without one, only a name that states its unit is
 * read: a duration called `total_handle` says nothing about its scale, so no
 * unit is claimed rather than a guessed one.
 */
export function unitFor(member: string, units: UnitMap = {}): string | null {
  const certified = units[member];
  if (certified) return unitLabel(certified);
  const tokens = tokensOf(member);
  for (const token of tokens) {
    const currency = CURRENCIES[token];
    if (currency) return currency;
  }
  if (tokens.some((token) => ["pct", "percent", "percentage"].includes(token))) {
    return "%";
  }
  if (
    tokens[0] === "count" ||
    tokens[tokens.length - 1] === "count" ||
    (tokens[0] === "number" && tokens[1] === "of")
  ) {
    return "count";
  }
  return null;
}

function withUnit(label: string, unit: string | null): string {
  if (!unit) return label;
  const words = label.toLowerCase().split(/\s+/);
  return words.includes(unit.toLowerCase()) ? label : `${label} (${unit})`;
}

/** A member's display label, carrying its unit when one is known. */
export function memberLabel(member: string, units: UnitMap = {}): string {
  return withUnit(humanise(member), unitFor(member, units));
}

/**
 * The title of an axis that carries one or more measures.
 *
 * A shared unit is stated once; mixed units are stated per measure so a reader
 * is never left guessing which scale they are reading.
 */
export function axisTitle(members: string[], units: UnitMap = {}): string {
  const found = [...new Set(members.map((member) => unitFor(member, units)))];
  if (found.length === 1) {
    return withUnit(members.map(humanise).join(" / "), found[0] ?? null);
  }
  return members.map((member) => memberLabel(member, units)).join(" / ");
}

/**
 * Applies an alpha to a hex colour.
 *
 * Series colours come from the stylesheet as hex, and a gradient needs the same
 * hue at two opacities. Anything that is not hex is returned unchanged, so a
 * token later written as `oklch(...)` degrades to a flat fill rather than to a
 * malformed colour ECharts would drop.
 */
function withAlpha(colour: string, alpha: number): string {
  const hex = colour.trim();
  const short = /^#([\da-f])([\da-f])([\da-f])$/i.exec(hex);
  const long = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
  if (!short && !long) return hex;
  const parts = short
    ? short.slice(1).map((part) => Number.parseInt(part + part, 16))
    : long!.slice(1).map((part) => Number.parseInt(part, 16));
  return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
}

/** A top-to-bottom fade of one colour, as ECharts accepts it declaratively. */
function fade(colour: string, from: number, to: number) {
  return {
    type: "linear",
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: withAlpha(colour, from) },
      { offset: 1, color: withAlpha(colour, to) },
    ],
  };
}

/**
 * A category label, from whatever the engine returned for that column.
 *
 * Guarded against a non-primitive because one arrived: Fabric dates reached the
 * client as ``{"nanosecondsDelta": 0}``, and ``String()`` renders that as
 * "[object Object]" once per point along the axis. The object carried no
 * timestamp, so nothing here can recover the date -- but printing a placeholder
 * says the value is missing, where "[object Object]" says the product is broken.
 */
export function categoryLabel(value: unknown, column = ""): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return "—";
  if (typeof value === "string") {
    const bucket = column ? granularLabel(value, column) : null;
    if (bucket) return bucket;
    const date = formatDate(value);
    if (date) return date;
  }
  const fiscal = column ? fiscalLabel(value, column) : null;
  if (fiscal) return fiscal;
  if (column && isMonthNumberColumn(column)) {
    return monthName(value) ?? String(value);
  }
  return String(value);
}

/**
 * Formats a figure for display.
 *
 * Recognises dates, fiscal periods, years and month numbers first; a plain
 * figure is then rounded by the shared rule in numberFormat.
 */
export function formatValue(value: unknown, column = ""): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") {
    const bucket = column ? granularLabel(value, column) : null;
    if (bucket) return bucket;
    const date = formatDate(value);
    if (date) return date;
  }
  const fiscal = column ? fiscalLabel(value, column) : null;
  if (fiscal) return fiscal;
  // A year is an identifier, not a quantity: "2,024" reads as a count.
  if (column && isYearColumn(column) && typeof value !== "object") {
    return String(value);
  }
  if (column && isMonthNumberColumn(column)) {
    const name = monthName(value);
    if (name) return name;
  }
  // Tables and scalar tiles use this formatter rather than `categoryLabel`.
  // Fabric dates currently arrive from Cube as `{"nanosecondsDelta": 0}` with
  // no timestamp left to display. Stringifying that object leaked
  // "[object Object]" into dashboard and report tables. A placeholder is honest:
  // it says the value is unavailable instead of presenting an implementation
  // detail as data.
  if (typeof value === "object") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return formatNumber(parsed);
}

/**
 * Formats a figure for an axis tick.
 *
 * A financial measure reaches nine digits, and `153,711,200` on every tick
 * consumes more width than the chart it labels. The reader is measuring a bar
 * against a gridline here, not reading the figure off, so three significant
 * characters and a magnitude are enough — the exact value is in the tooltip.
 */
export function formatCompact(value: unknown): string {
  // Coerced deliberately rather than by `Number`, which turns null and false
  // into a finite zero and would print a missing figure as one.
  const parsed =
    value === null ||
    value === undefined ||
    value === "" ||
    typeof value === "boolean"
      ? Number.NaN
      : Number(value);
  // Anything this cannot shorten is a job for the full formatter, which already
  // knows how to say "missing" and how to pass text through.
  if (!Number.isFinite(parsed)) return formatValue(value);
  if (parsed === 0) return "0";

  const magnitude = Math.abs(parsed);
  const units: [number, string][] = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];
  for (const [size, suffix] of units) {
    if (magnitude >= size) {
      const scaled = parsed / size;
      // One decimal below ten, none above: 1.2M reads, 12.4M is noise.
      const digits = Math.abs(scaled) < 10 ? 1 : 0;
      return `${scaled.toFixed(digits).replace(/\.0$/, "")}${suffix}`;
    }
  }
  return formatValue(parsed);
}

const GENERIC_LEAVES = new Set([
  "name", "id", "code", "key", "description", "type", "label", "title",
]);

/** Turns a member or column alias into a label a reader recognises. */
export function humanise(column: string): string {
  // A table column of "Jan 2025" buckets is headed like the chart axis.
  const granularity = granularityOf(column);
  if (granularity && GRANULAR_TITLES[granularity]) return GRANULAR_TITLES[granularity];
  const parts = column.split(".");
  let label = parts.pop() ?? column;
  // "fact_interaction.count" is a count of interactions, not a bare "Count".
  if (label === "count" && parts.length > 0) {
    const entity = (parts.pop() ?? "").replace(/^(dim|fact)_/, "");
    label = `${entity}_count`;
  } else if (GENERIC_LEAVES.has(label) && /^(dim|fact)_./.test(parts[parts.length - 1] ?? "")) {
    // "dim_agent.name" is the agent's name; a bare "Name" says nothing.
    label = `${(parts[parts.length - 1] ?? "").replace(/^(dim|fact)_/, "")}_${label}`;
  }
  return label
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}

/**
 * Removes a redundant month number when the result already includes month name.
 *
 * The number is useful for query ordering but not for display. Keeping both
 * makes a table look like it has two different time dimensions when they
 * represent the same month.
 */
export function displayColumns(columns: string[]): string[] {
  if (!columns.some(isMonthNameColumn)) return columns;
  return columns.filter((column) => !isMonthNumberColumn(column));
}

/** Keeps a long category from pushing the plot area off the panel. */
function truncate(label: string): string {
  return label.length > CATEGORY_LABEL_LIMIT
    ? `${label.slice(0, CATEGORY_LABEL_LIMIT - 1)}…`
    : label;
}

/**
 * How far to tilt the category labels.
 *
 * Length matters as much as count. Five categories named "Leasehold
 * Improvements" collide exactly as badly as twelve named "Jan", and with
 * `hideOverlap` the collision is silent — ECharts simply drops every other
 * label, so the chart looks fine and is missing half its axis.
 */
function categoryRotation(categories: string[]): number {
  const longest = categories.reduce(
    (most, label) => Math.max(most, truncate(label).length),
    0,
  );
  if (categories.length > 6 || longest > 12) return 30;
  return longest > 8 && categories.length > 3 ? 18 : 0;
}

/** Tooltip chrome, shared by every chart type. */
function tooltip(theme: ChartTheme, trigger: "axis" | "item", pointer?: string) {
  return {
    trigger,
    ...(pointer
      ? {
          axisPointer: {
            type: pointer,
            lineStyle: { color: theme.axis, width: 1, type: "dashed" },
            shadowStyle: { color: withAlpha(theme.axis, 0.1) },
          },
        }
      : {}),
    backgroundColor: theme.tooltipBg,
    borderColor: theme.tooltipLine,
    borderWidth: 1,
    padding: [8, 11],
    textStyle: { color: theme.tooltipInk, fontSize: 12, fontFamily: FONT },
    extraCssText:
      "border-radius:9px;box-shadow:0 10px 28px rgba(7,31,55,.16);backdrop-filter:saturate(140%);",
    valueFormatter: (value: unknown) => formatValue(value),
  };
}

/**
 * The palette colour for one series, wrapping when there are more series than
 * colours. Falls back to the chart ink so a series is never drawn colourless.
 */
function colourAt(theme: ChartTheme, index: number): string {
  return theme.palette[index % theme.palette.length] ?? theme.ink;
}

/** Legend chrome. Hidden for a single series, where it only repeats the title. */
function legend(theme: ChartTheme, count: number, atBottom = false) {
  return {
    show: count > 1,
    type: "scroll",
    icon: "circle",
    itemWidth: 8,
    itemHeight: 8,
    itemGap: 14,
    ...(atBottom ? { bottom: 0, left: "center" } : { top: 0, left: 0 }),
    textStyle: { color: theme.axis, fontSize: 11, fontFamily: FONT },
    pageTextStyle: { color: theme.axis, fontSize: 11 },
    pageIconColor: theme.axis,
    pageIconInactiveColor: theme.grid,
  };
}

const GRANULARITIES = [
  "second", "minute", "hour", "day", "week", "month", "quarter", "year",
] as const;

/**
 * The result column an encoding refers to.
 *
 * Cube returns a time dimension requested at a granularity under the member
 * name plus that granularity (`fact_interaction.date.month`), so an encoding
 * naming the bare member found no column and every category read "—".
 */
export function resolveColumn(name: string, columns: string[]): string {
  if (!name || columns.includes(name)) return name;
  const granular = columns.find((column) => column.startsWith(`${name}.`));
  if (granular) return granular;
  const base = name.replace(
    new RegExp(`\\.(${GRANULARITIES.join("|")})$`),
    "",
  );
  return base !== name && columns.includes(base) ? base : name;
}

function granularityOf(column: string): string | null {
  const suffix = column.split(".").pop() ?? "";
  return (GRANULARITIES as readonly string[]).includes(suffix) &&
    column.split(".").length >= 3
    ? suffix
    : null;
}

/** What a bucketed time axis shows, named the way its labels read. */
const GRANULAR_TITLES: Record<string, string> = {
  year: "Year",
  quarter: "Quarter and Year",
  month: "Month and Year",
  week: "Week",
  day: "Date",
  hour: "Hour",
  minute: "Time",
  second: "Time",
};

/** A bucketed timestamp as its bucket reads: "Dec 2025", "Q4 2025", "2025". */
function granularLabel(value: string, column: string): string | null {
  const granularity = granularityOf(column);
  if (!granularity) return null;
  const match = ISO_DATE.exec(value.trim());
  if (!match) return null;
  const [, year, month] = match;
  const monthIndex = Number(month) - 1;
  if (granularity === "year") return year ?? null;
  if (granularity === "quarter") return `Q${Math.floor(monthIndex / 3) + 1} ${year}`;
  if (granularity === "month") return `${MONTH_NAMES[monthIndex]} ${year}`;
  if (granularity === "week") return `Week of ${formatDate(value)}`;
  return null;
}

function isTemporalColumn(
  column: string,
  rows: Record<string, unknown>[],
): boolean {
  let seen = 0;
  for (const row of rows.slice(0, 20)) {
    const value = row[column];
    if (value === null || value === undefined || value === "") continue;
    if (typeof value !== "string" || !ISO_DATE.test(value.trim())) return false;
    seen += 1;
  }
  return seen > 0;
}

export function buildOption(
  chartType: ChartType,
  encoding: PanelEncoding,
  columns: string[],
  rows: Record<string, unknown>[],
  theme: ChartTheme = chartTheme(),
  units: UnitMap = {},
): EChartsCoreOption {
  const x = resolveColumn(encoding.x || columns[0] || "", columns);
  const series = resolveColumn(encoding.series ?? "", columns);
  const declared = encoding.y
    .map((column) => resolveColumn(column, columns))
    .filter((column) => columns.includes(column));
  // An encoding can name a column the result does not contain, which happens
  // when a contract moves underneath a saved panel. Falling back to the
  // non-dimension columns draws something honest rather than nothing.
  const measures =
    declared.length > 0
      ? declared
      : columns.filter((column) => column !== x && column !== series);
  const primary = measures[0] ?? "";
  const xGranularity = granularityOf(x);
  const fiscalAxis = isFiscalOrdinalColumn(x);
  // "Hours Date (month)" names the column; the reader is reading months.
  const xTitle = xGranularity
    ? GRANULAR_TITLES[xGranularity] ?? humanise(x)
    : fiscalAxis && !leafOf(x).includes("fiscal")
      ? `Fiscal ${humanise(x)}`
      : humanise(x);
  const monthAxis = isMonthNameColumn(x) || isMonthNumberColumn(x) || fiscalAxis;
  // Trends read left to right in time, whatever order the engine returned.
  const ordered = monthAxis
    ? chronologicalRows(rows, columns)
    : isTemporalColumn(x, rows)
      ? [...rows].sort((a, b) =>
          String(a[x] ?? "").localeCompare(String(b[x] ?? "")),
        )
      : rows;

  const motion = theme.animate
    ? { animation: true, animationDuration: 480, animationEasing: "cubicOut" as const }
    : { animation: false };
  const base = {
    color: theme.palette,
    textStyle: { color: theme.ink, fontFamily: FONT },
    // Generates a text description of the chart for screen readers.
    aria: { enabled: true },
    ...motion,
  };

  if (chartType === "pie") {
    // A slice with no value is omitted rather than drawn as a zero share.
    const slices = ordered.flatMap((row) => {
      const value = toNumber(row[primary]);
      return value === null
        ? []
        : [{ name: categoryLabel(row[x], x), value }];
    });
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);

    return {
      ...base,
      tooltip: tooltip(theme, "item"),
      legend: legend(theme, slices.length, true),
      // The hole in a donut is dead space, and the one figure every reader of a
      // breakdown wants is the total it breaks down.
      title: {
        text: formatCompact(total),
        subtext: memberLabel(primary, units),
        left: "center",
        top: "42%",
        textStyle: {
          color: theme.ink,
          fontSize: 21,
          fontWeight: 650,
          fontFamily: FONT,
        },
        subtextStyle: { color: theme.axis, fontSize: 10.5, fontFamily: FONT },
      },
      series: [
        {
          type: "pie",
          radius: ["54%", "78%"],
          center: ["50%", "48%"],
          avoidLabelOverlap: true,
          // Slices are separated by a gap in the panel colour rather than by a
          // stroke, so the ring reads as segments instead of as an outlined pie.
          itemStyle: {
            borderColor: theme.surface,
            borderWidth: 2,
            borderRadius: 4,
          },
          // The legend names the slices. Labels around the ring collide at
          // panel size, and leader lines make a small chart look busy.
          label: { show: false },
          labelLine: { show: false },
          emphasis: {
            scaleSize: 6,
            itemStyle: { shadowBlur: 14, shadowColor: withAlpha(theme.ink, 0.25) },
          },
          data: slices,
        },
      ],
    };
  }

  const type = chartType === "line" ? "line" : "bar";
  let categories: string[];
  let seriesList: Record<string, unknown>[];

  /** The chrome one series carries, given its position in the palette. */
  function shape(index: number) {
    const colour = colourAt(theme, index);
    if (type === "bar") {
      return {
        barMaxWidth: BAR_MAX_WIDTH,
        // Rounded only at the top: the baseline is a real edge and rounding it
        // would lift the bar off its own axis.
        itemStyle: { borderRadius: [4, 4, 0, 0], color: fade(colour, 1, 0.72) },
        emphasis: { focus: "series", itemStyle: { color: colour } },
      };
    }
    return {
      smooth: true,
      // Symbols on every point turn a dense line into a dotted mess, but the
      // hit target has to stay large enough to hover.
      showSymbol: false,
      symbol: "circle",
      symbolSize: 7,
      lineStyle: { width: 2.5, cap: "round", join: "round" },
      emphasis: { focus: "series", scale: 1.5 },
    };
  }

  // Measures in two different units get an axis each; three or more share one
  // axis whose title states every unit, because a third scale is unreadable.
  const unitOf = (measure: string) => unitFor(measure, units);
  const axisUnits = [...new Set(measures.map(unitOf))];
  const dualAxis = !series && axisUnits.length === 2;
  const axisFor = (measure: string) =>
    dualAxis ? axisUnits.indexOf(unitOf(measure)) : 0;

  if (series) {
    // A series column pivots long-form rows into one line per member, which is
    // the shape a breakdown-over-time arrives in from the engine.
    // Ordered by the axis alone: when the year is the series, ordering by year
    // first would list each period in the year it first appears.
    const alongAxis = monthAxis ? chronologicalRows(ordered, [x]) : ordered;
    categories = [...new Set(alongAxis.map((row) => categoryLabel(row[x], x)))];
    const members = [
      ...new Set(ordered.map((row) => categoryLabel(row[series], series))),
    ];
    seriesList = members.map((member, index) => ({
      name: member,
      type,
      ...shape(index),
      data: categories.map((category) => {
        const match = ordered.find(
          (row) =>
            categoryLabel(row[x], x) === category &&
            categoryLabel(row[series], series) === member,
        );
        // Null rather than zero: a gap in a series is missing data, and
        // drawing it as zero invents a figure the query never returned.
        return match ? toNumber(match[primary]) : null;
      }),
    }));
  } else {
    categories = ordered.map((row) => categoryLabel(row[x], x));
    seriesList = measures.map((measure, index) => ({
      name: memberLabel(measure, units),
      type,
      ...shape(index),
      yAxisIndex: axisFor(measure),
      data: ordered.map((row) => toNumber(row[measure])),
    }));
  }

  // containLabel reserves room for tick labels but not for axis titles, so a
  // fixed nameGap placed the titles outside the canvas and they were clipped.
  // Each title sits just beyond its own labels, and the grid keeps the room.
  const axisValues = (index: number): number[] =>
    seriesList
      .filter((item) => (item.yAxisIndex ?? 0) === index)
      .flatMap((item) => (item.data as (number | null)[]) ?? [])
      .filter((value): value is number => value !== null);
  const yNameGap = (index: number) =>
    Math.ceil(valueLabelWidth(axisValues(index))) + TITLE_CLEARANCE;

  const valueAxis = (
    title: string,
    position: "left" | "right",
    index: number,
  ) => ({
    type: "value",
    name: title,
    nameLocation: "middle",
    nameGap: yNameGap(index),
    nameRotate: 90,
    position,
    nameTextStyle: {
      color: theme.axis,
      fontSize: 11,
      fontWeight: 600,
      fontFamily: FONT,
    },
    // The split lines already say where the axis is, so the axis line and its
    // ticks are two more marks drawn over the same information.
    axisTick: { show: false },
    axisLine: { show: false },
    axisLabel: {
      color: theme.axis,
      fontSize: 11,
      fontFamily: FONT,
      formatter: formatCompact,
    },
    splitLine: {
      show: position === "left",
      lineStyle: { color: theme.grid, type: "dashed" },
    },
  });
  // With several measures on one axis the legend already names them. Repeating
  // every name made the title longer than the plot, so it ran into the legend
  // and the category labels; the axis keeps only their shared unit.
  const titleFor = (onAxis: string[]): string => {
    if (onAxis.length < 2) return axisTitle(onAxis, units);
    const shared = [...new Set(onAxis.map(unitOf))];
    const unit = shared.length === 1 ? shared[0] : null;
    return unit ? unit.charAt(0).toUpperCase() + unit.slice(1) : "";
  };
  const yTitles = dualAxis
    ? axisUnits.map((unit) => titleFor(measures.filter((measure) => unitOf(measure) === unit)))
    : [titleFor(series ? [primary] : measures)];
  const yAxis = dualAxis
    ? yTitles.map((title, index) => valueAxis(title, index === 0 ? "left" : "right", index))
    : valueAxis(yTitles[0] ?? "", "left", 0);
  /** Room beside the plot for an axis's labels, and its title when it has one. */
  const sideRoom = (index: number): number =>
    yTitles[index]
      ? yNameGap(index) + TITLE_ROOM / 2
      : yNameGap(index) - TITLE_CLEARANCE + Y_LABEL_MARGIN + 4;

  // The axis title already says "Week", so each label need only give the date.
  const axisLabel = (category: string): string =>
    truncate(xGranularity === "week" ? category.replace(/^Week of /, "") : category);
  const shownCategories = categories.map(axisLabel);
  const zoom = categories.length > ZOOM_CATEGORY_THRESHOLD;
  const rotation = categoryRotation(shownCategories);
  const xNameGap =
    Math.ceil(categoryLabelHeight(shownCategories, rotation)) + X_LABEL_MARGIN + 8;
  // A tilted label ends at its tick and runs back to the left, so the first
  // one reaches past the plot's left edge by its own projected width.
  const firstLabelReach = rotation
    ? (shownCategories[0]?.length ?? 0) * CHAR_WIDTH * Math.cos((rotation * Math.PI) / 180) -
      (type === "bar" ? BAR_MAX_WIDTH / 2 : 0) + 4
    : 0;
  const left = Math.ceil(Math.max(sideRoom(0), firstLabelReach));

  // A single line gets a faded area beneath it, which gives the eye a magnitude
  // as well as a trend. Several would overlap into mud, so they stay as lines.
  const only = seriesList.length === 1 ? seriesList[0] : undefined;
  if (type === "line" && only) {
    only.areaStyle = { opacity: 1, color: fade(colourAt(theme, 0), 0.24, 0) };
  }

  const multi = seriesList.length > 1;
  // The margins are the titles' own gaps plus their line height. With
  // containLabel the grid shrank to the labels ECharts measured, but each title
  // sat at the gap estimated here; whenever the real labels were narrower than
  // the estimate ("5M" against a "5.6M" sample) the title fell off the canvas.
  return {
    ...base,
    grid: {
      left,
      right: dualAxis ? sideRoom(1) : 16,
      top: multi ? 30 : 12,
      bottom: xNameGap + TITLE_ROOM / 2 + (zoom ? 22 : 0),
      containLabel: false,
    },
    // A crowded axis scrolls rather than hiding every other label.
    ...(zoom
      ? {
          dataZoom: [
            { type: "inside", xAxisIndex: 0 },
            { type: "slider", xAxisIndex: 0, height: 14, bottom: 0 },
          ],
        }
      : {}),
    tooltip: tooltip(theme, "axis", type === "bar" ? "shadow" : "line"),
    // Aligned with the plot rather than the canvas edge, where the y-axis
    // title runs up the side.
    legend: { ...legend(theme, seriesList.length), left },
    xAxis: {
      type: "category",
      data: categories,
      name: xTitle,
      nameLocation: "middle",
      nameGap: xNameGap,
      nameTextStyle: {
        color: theme.axis,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: FONT,
      },
      // A line should meet the edges of its plot area; a bar needs room for
      // half its own width at each end.
      boundaryGap: type === "bar",
      axisTick: { show: false },
      axisLine: { lineStyle: { color: theme.grid } },
      axisLabel: {
        color: theme.axis,
        fontSize: 11,
        fontFamily: FONT,
        margin: X_LABEL_MARGIN,
        rotate: rotation,
        hideOverlap: true,
        formatter: axisLabel,
      },
    },
    yAxis,
    series: seriesList,
  };
}
